import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { userService, cookieService } from '../../_services';
import ValidationErrorToast from '../../components/validationErrorToast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faChartLine, faArrowUp, faArrowDown, faSyncAlt, faPhone, faFileInvoiceDollar } from '@fortawesome/free-solid-svg-icons';
import { STRING_CONSTANTS } from '../../utils/constants/stringConstants';

const CBADashboard = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [whitelabelStats, setWhitelabelStats] = useState(null);
    const [customerStats, setCustomerStats] = useState(null);
    const [activityFeed, setActivityFeed] = useState([]);
    const whitelabelDomain = window.location.hostname;

    // get user info
    const userCookie = cookieService?.getCookie(STRING_CONSTANTS?.cookieStrings?.user);
    const isUserLoggedIn = cookieService.getCookie(STRING_CONSTANTS.cookieStrings.token);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        setIsLoading(true);
        try {
            const response = await userService.aiRequest({ 
                method: 'get-whitelabel-dashboard',
                data: { whitelabel_domain: whitelabelDomain, user: userCookie?.user?.email }
            }, true);
            
            if (response.data) {
                setWhitelabelStats(response.data.whitelabelStats);
                setCustomerStats(response.data.customerStats);
                setActivityFeed(response.data.activityFeed);
            }
        } catch (error) {
            ValidationErrorToast("Error", "Could not load dashboard statistics.");
            //window.location.href = '/';
            console.error("Load stats error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const styles: { [key: string]: React.CSSProperties } = {
        pageContainer: { backgroundColor: '#F8F9FA', color: '#141414', minHeight: '100vh', display: 'flex', flexDirection: 'column' },
        topBar: { background: '#FFFFFF', padding: '16px 30px', borderBottom: '1px solid #E0E0E0' },
        topBarTitle: { color: '#141414', margin: 0, fontSize: '24px', fontWeight: '700' },
        main: { flex: 1, overflowY: 'auto', padding: '24px 30px' },
        sectionTitle: { fontSize: '18px', fontWeight: '600', color: '#555555', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '16px' },
        grid: { display: 'grid', gap: '24px' },
        twoColumnGrid: { gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))' },
        fourColumnGrid: { gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' },
        statTile: { background: '#FFFFFF', borderRadius: '12px', padding: '24px', border: '1px solid #E0E0E0', display: 'flex', flexDirection: 'column', gap: '12px' },
        statHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
        statLabel: { color: '#555555', fontSize: '14px', fontWeight: '500' },
        statIcon: { fontSize: '18px', color: '#0065FF' },
        statValue: { color: '#141414', fontSize: '32px', fontWeight: '800' },
        statSubValue: { color: '#6B7280', fontSize: '14px' },
        activityCard: { background: '#FFFFFF', borderRadius: '12px', padding: '24px', border: '1px solid #E0E0E0' },
        activityList: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '20px' },
        activityItem: { display: 'flex', alignItems: 'center', gap: '16px' },
        activityIcon: { width: '40px', height: '40px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F0F7FF', color: '#0065FF', fontSize: '16px' },
        activityText: { flex: 1, color: '#242424', fontSize: '14px' },
        activityTime: { color: '#6B7280', fontSize: '12px', whiteSpace: 'nowrap' },
        loadingOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255, 255, 255, 0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10000 },
    };

    const ActivityIcon = ({ type }) => {
        const iconMap = { call: faPhone, payment: faFileInvoiceDollar };
        return (
            <div style={styles.activityIcon}>
                <FontAwesomeIcon icon={iconMap[type] || faSyncAlt} />
            </div>
        );
    };
    
    if (isLoading) {
        return <div style={styles.loadingOverlay}><div className="spinner-border text-primary" role="status"></div></div>;
    }

    return (
        <div style={styles.pageContainer}>
            <Helmet><title>Dashboard</title></Helmet>
            <div style={styles.topBar}>
                <h1 style={styles.topBarTitle}>Dashboard</h1>
            </div>
            <main style={styles.main}>
                <h2 style={styles.sectionTitle}>Whitelabel Overview</h2>
                <div style={{...styles.grid, ...styles.fourColumnGrid, marginBottom: '40px'}}>
                    <div style={styles.statTile}>
                        <div style={styles.statHeader}><span style={styles.statLabel}>Customers</span><FontAwesomeIcon icon={faUsers} style={styles.statIcon} /></div>
                        <div style={styles.statValue}>{whitelabelStats?.whitelabelCustomers || 0}</div>
                    </div>
                    {/* <div style={styles.statTile}>
                        <div style={styles.statHeader}><span style={styles.statLabel}>MRR</span><FontAwesomeIcon icon={faSyncAlt} style={styles.statIcon} /></div>
                        <div style={styles.statValue}>${whitelabelStats?.mrr?.toLocaleString() || 0}</div>
                    </div>
                    <div style={styles.statTile}>
                        <div style={styles.statHeader}><span style={styles.statLabel}>ARR</span><FontAwesomeIcon icon={faChartLine} style={styles.statIcon} /></div>
                        <div style={styles.statValue}>${whitelabelStats?.arr?.toLocaleString() || 0}</div>
                    </div> */}
                    <div style={styles.statTile}>
                        <div style={styles.statHeader}><span style={styles.statLabel}>Calls Today</span><FontAwesomeIcon icon={faPhone} style={styles.statIcon} /></div>
                        <div style={styles.statValue}>{customerStats?.callsToday || 0}</div>
                    </div>
                </div>

                <div style={{...styles.grid, ...styles.twoColumnGrid}}>
                    <div>
                        <h2 style={styles.sectionTitle}>Customer Statistics</h2>
                        <div style={{...styles.grid, gridTemplateColumns: '1fr 1fr', gap: '16px'}}>
                            <div style={styles.statTile}><span style={styles.statLabel}>Total Signups</span><div style={styles.statValue}>{customerStats?.signups?.toLocaleString() || 0}</div></div>
                            <div style={styles.statTile}><span style={styles.statLabel}>Invoices Paid</span><div style={styles.statValue}>{customerStats?.invoicesPaid?.toLocaleString() || 0}</div></div>
                            <div style={{...styles.statTile, gridColumn: '1 / -1'}}><span style={styles.statLabel}>Amount Collected</span><div style={styles.statValue}>${customerStats?.amountCollected?.toLocaleString(undefined, {minimumFractionDigits: 2}) || '0.00'}</div></div>
                        </div>
                    </div>
                    <div style={styles.activityCard}>
                        <h2 style={styles.sectionTitle}>Recent Activity</h2>
                        {activityFeed.length > 0 ? (
                            <ul style={styles.activityList}>
                                {activityFeed.map(item => (
                                    <li key={item.id} style={styles.activityItem}>
                                        <ActivityIcon type={item.type} />
                                        <span style={styles.activityText}>
                                            <strong>{item.customer}</strong> 
                                            {item.type === 'payment' && item.amount ? ` - $${item.amount.toFixed(2)}` : ''}
                                        </span>
                                        <span style={styles.activityTime}>{item.time}</span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p style={{color: '#6B7280', textAlign: 'center', padding: '20px'}}>No recent activity</p>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
};

export default CBADashboard;